import { useEffect, useMemo, useState } from 'react'
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  Checkbox,
  ListItemText,
  SelectProps,
  FormControl,
  ListItemIcon,
  InputAdornment,
  ListSubheader,
  TextField,
  SxProps,
  Theme,
  Button,
  InputLabelProps,
} from '@mui/material'
import {
  FieldError,
  FieldPath,
  FieldValues,
  UseFormRegister,
} from 'react-hook-form'
import {
  CheckBoxOutlineBlank, CheckBox, IndeterminateCheckBox, Search,
} from '@mui/icons-material'

interface SelectOption {
  id: number
  description: string
}

interface MultiSelectProps<T extends FieldValues, K extends FieldPath<T>> extends SelectProps {
  register: UseFormRegister<T>;
  inputName: K;
  label: string;
  options: SelectOption[];
  errors: FieldError[] | undefined;
  dataTestId: string;
  errorMessage?: string;
  clearInputOnChange?: unknown;
  clearInputOnChangeMode?: 'clear' | 'useDefault'
  required?: boolean;
  disabled?: boolean;
  optionProperty?: string;
  selectAll?: boolean;
  searchBar?: boolean;
  searchBarDataTestId?: string;
  maxHeight?: string;
  fullWidth?: boolean;
  width?: string;
  formControlSx?: SxProps<Theme> | undefined
  defaultOptions?: unknown[]
  closeButton?: boolean
  customInputLabelProps?: InputLabelProps
}

const MultipleSelect = <T extends FieldValues, K extends FieldPath<T>>(
  multiSelectProps: MultiSelectProps<T, K>,
) => {
  const {
    register,
    inputName,
    label,
    options,
    errors,
    dataTestId,
    errorMessage = 'Campo obrigatório',
    clearInputOnChange,
    clearInputOnChangeMode = 'clear',
    size = 'small',
    required = false,
    disabled = false,
    optionProperty = 'description',
    selectAll = false,
    searchBar = false,
    searchBarDataTestId,
    maxHeight,
    fullWidth,
    width = !fullWidth ? '16rem' : undefined,
    formControlSx,
    defaultOptions = [],
    closeButton = false,
    customInputLabelProps = undefined,
    ...props
  } = multiSelectProps

  const [open, setOpen] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<unknown[]>(defaultOptions)
  const [searchText, setSearchText] = useState('')

  const containsText = (_text: any, _searchText: string) => _text.toString().toLowerCase().indexOf(_searchText.toLowerCase()) > -1

  const displayedOptions = useMemo(() => options
    .filter((option) => containsText(option.description, searchText)),
  [options, searchText])

  const selectedDisplayedOptionsLength = selectedOptions
    .filter((selectedOption) => displayedOptions
      .map((option) => option[optionProperty]).includes(selectedOption))
    .length

  const getSelectedOptionDescription = (_selectedOption: any) => (optionProperty === 'id'
    ? options.find((option) => option.id === _selectedOption)?.description
    : _selectedOption)

  const labelId = `${label}-multiple-select-label`

  useEffect(() => {
    if (clearInputOnChange) {
      setSelectedOptions(clearInputOnChangeMode === 'clear' ? [] : defaultOptions)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearInputOnChange])

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setSearchText('')
  }

  const handleChange = (event: any) => {
    const { value } = event.target
    if (selectAll && value[value.length - 1] === 'all') {
      if (displayedOptions.length === options.length && selectedOptions.length === options.length) {
        setSelectedOptions([])
      } else if (displayedOptions.length === selectedDisplayedOptionsLength) {
        setSelectedOptions(selectedOptions
          .filter((selectedOption) => !containsText(getSelectedOptionDescription(selectedOption), searchText)))
      } else if (options.length > displayedOptions.length && displayedOptions.length > selectedDisplayedOptionsLength) {
        setSelectedOptions([
          ...selectedOptions
            .filter((selectedOption) => !containsText(getSelectedOptionDescription(selectedOption), searchText)),
          ...options
            .filter((option) => containsText((option as SelectOption).description, searchText))
            .map((option) => option[optionProperty]),
        ])
      } else {
        setSelectedOptions(options
          .filter((option) => containsText((option as SelectOption).description, searchText))
          .map((option) => option[optionProperty]))
      }
      return
    }
    setSelectedOptions(value)
  }

  return (
    <FormControl size={size} sx={{ width, ...formControlSx }} fullWidth={fullWidth} data-testid={`form-${dataTestId}`}>
      <InputLabel id={labelId} {...customInputLabelProps}>{label}</InputLabel>
      <Select
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple
        inputProps={{ ...register(inputName, { required: required && errorMessage }), 'data-testid': `input-${dataTestId}` }}
        id={inputName}
        labelId={labelId}
        renderValue={(selected: any) => {
          const selectedDescriptions = selected.map((selectedProperty: number | string) => {
            const selectedOption = options.find((option) => option[optionProperty] === selectedProperty)
            return selectedOption ? selectedOption.description : ''
          })
          return selectedDescriptions.join(', ')
        }}
        value={selectedOptions}
        onChange={handleChange}
        input={<OutlinedInput label={label} notched={customInputLabelProps?.shrink} />}
        error={!!errors && required}
        disabled={disabled}
        MenuProps={{
          autoFocus: !searchBar,
          PaperProps: {
            sx: {
              maxHeight,
              width,
              '& .MuiList-root': {
                padding: 0,
              },
            },
          },
        }}
        {...props}
      >
        {(searchBar || closeButton)
        && (
          <ListSubheader sx={{ padding: '.6rem' }}>
            {searchBar && (
              <TextField
                autoFocus
                autoComplete='off'
                size='small'
                id={`${inputName}-search-bar`}
                label='Pesquisar'
                variant='standard'
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search />
                    </InputAdornment>
                  ),
                  inputProps: {
                    'data-testid': searchBarDataTestId,
                  },
                }}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') {
                    e.stopPropagation()
                  }
                }}
              />
            )}
            {closeButton && (
              <Button
                variant='outlined'
                onClick={handleClose}
                size='small'
                fullWidth
              >
                Selecionar
              </Button>
            )}
          </ListSubheader>
        )}
        {selectAll
        && (
        <MenuItem
          value='all'
        >
          <ListItemIcon>
            <Checkbox
              icon={<CheckBoxOutlineBlank fontSize='small' />}
              checkedIcon={<CheckBox fontSize='small' />}
              indeterminateIcon={<IndeterminateCheckBox fontSize='small'/>}
              checked={
                selectedDisplayedOptionsLength > 0 && selectedDisplayedOptionsLength === displayedOptions.length
              }
              indeterminate={
                selectedDisplayedOptionsLength > 0 && selectedDisplayedOptionsLength < displayedOptions.length
              }
            />
          </ListItemIcon>
          <ListItemText
            primary='Todos'
          />
        </MenuItem>
        )}
        {displayedOptions?.map((option) => (
          <MenuItem key={option.id} value={option[optionProperty]} data-testid={`option-${option.id}-${dataTestId}`} >
            <Checkbox
              icon={<CheckBoxOutlineBlank fontSize='small' />}
              checkedIcon={<CheckBox fontSize='small' />}
              checked={selectedOptions.indexOf(option[optionProperty]) > -1}
            />
            <ListItemText primary={option.description} />
          </MenuItem>
        ))}
      </Select>
      {errors && required && !disabled && (
        <FormHelperText sx={{ color: 'red' }}>{ errorMessage }</FormHelperText>
      )}
    </FormControl>
  )
}

export default MultipleSelect
